import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

/**
 * Session readiness states:
 * - 'checking': Initial state, checking for persisted session
 * - 'ready': Session check complete (user may or may not be signed in)
 * - 'unknown': Should rarely be used, fallback state
 */
type ReadyState = 'checking' | 'ready' | 'unknown';

interface AppSessionContextValue {
  ready: ReadyState;
  session: Session | null;
  isAuthenticated: boolean;
}

const AppSessionContext = createContext<AppSessionContextValue | undefined>(undefined);

interface AppSessionProviderProps {
  children: ReactNode;
}

/**
 * Centralized session provider that manages authentication state and readiness.
 * 
 * Key behaviors:
 * - Treats INITIAL_SESSION as "checking", NOT as a sign-in/sign-out event
 * - Only marks as 'ready' after getSession() completes
 * - All auth routing decisions should wait for ready === 'ready'
 * - Prevents race conditions and premature redirects
 */
export function AppSessionProvider({ children }: AppSessionProviderProps) {
  const [ready, setReady] = useState<ReadyState>('checking');
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    let mounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    console.log('[AppSessionProvider] Initializing - fetching persisted session');

    // Add timeout to prevent hanging forever
    timeoutId = setTimeout(() => {
      if (mounted && ready === 'checking') {
        console.error('[AppSessionProvider] getSession timeout - marking as ready with no session');
        setSession(null);
        setReady('ready');
      }
    }, 10000); // 10 second timeout

    // Step 1: Fetch the persisted session (this is the ONLY reliable way to know initial state)
    supabase.auth.getSession()
      .then(({ data, error }) => {
        if (timeoutId) clearTimeout(timeoutId);
        if (!mounted) return;

        if (error) {
          console.error('[AppSessionProvider] Error fetching session:', error);
          setSession(null);
          setReady('ready');
          return;
        }

        console.log('[AppSessionProvider] Session loaded:', {
          hasSession: !!data.session,
          userId: data.session?.user?.id,
          expiresAt: data.session?.expires_at
        });

        setSession(data.session ?? null);
        setReady('ready');
      })
      .catch((error) => {
        if (timeoutId) clearTimeout(timeoutId);
        console.error('[AppSessionProvider] Failed to get session:', error);
        if (!mounted) return;
        // Mark as ready even on error so app doesn't hang
        setSession(null);
        setReady('ready');
      });

    // Step 2: Listen for auth state changes
    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
        console.log('[AppSessionProvider] Auth state changed:', event, {
          hasSession: !!newSession,
          userId: newSession?.user?.id
        });

        // CRITICAL: Ignore INITIAL_SESSION - it fires before getSession() completes
        // and does NOT represent a real sign-in or sign-out event
        if (event === 'INITIAL_SESSION') {
          console.log('[AppSessionProvider] Ignoring INITIAL_SESSION event');
          return;
        }

        // Handle real auth events
        switch (event) {
          case 'SIGNED_IN':
            console.log('[AppSessionProvider] User signed in');
            setSession(newSession);
            setReady('ready');
            break;
          
          case 'SIGNED_OUT':
            console.log('[AppSessionProvider] User signed out');
            setSession(null);
            setReady('ready');
            break;
          
          case 'TOKEN_REFRESHED':
            console.log('[AppSessionProvider] Token refreshed');
            setSession(newSession);
            // Already ready, no need to change state
            break;
          
          case 'USER_UPDATED':
            console.log('[AppSessionProvider] User updated');
            setSession(newSession);
            // Already ready, no need to change state
            break;
          
          case 'PASSWORD_RECOVERY':
            console.log('[AppSessionProvider] Password recovery');
            setSession(newSession);
            setReady('ready');
            break;
          
          default:
            console.log('[AppSessionProvider] Unhandled auth event:', event);
        }
      });

      return () => {
        mounted = false;
        if (timeoutId) clearTimeout(timeoutId);
        subscription.unsubscribe();
      };
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      console.error('[AppSessionProvider] Error setting up auth listener:', error);
      // If listener setup fails, still mark as ready so app doesn't hang
      setReady('ready');
      return () => {
        mounted = false;
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [ready]);

  const contextValue: AppSessionContextValue = {
    ready,
    session,
    isAuthenticated: !!session,
  };

  return (
    <AppSessionContext.Provider value={contextValue}>
      {children}
    </AppSessionContext.Provider>
  );
}

/**
 * Hook to access the app session context.
 * 
 * Usage:
 * ```tsx
 * const { ready, session, isAuthenticated } = useAppSession();
 * 
 * // Wait for ready state before making decisions
 * useEffect(() => {
 *   if (ready !== 'ready') return;
 *   
 *   if (!isAuthenticated) {
 *     navigate('/auth');
 *   }
 * }, [ready, isAuthenticated]);
 * ```
 */
export function useAppSession() {
  const context = useContext(AppSessionContext);
  
  if (context === undefined) {
    throw new Error('useAppSession must be used within an AppSessionProvider');
  }
  
  return context;
}
